import { Filesystem, Directory, Encoding } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

interface LogEntry {
  timestamp: string
  level: LogLevel
  tag: string
  message: string
  data?: any
}

class Logger {
  private logs: LogEntry[] = []
  private maxLogSize = 1000
  private logLevel = LogLevel.DEBUG
  private logFileName = 'matt-debug.log'

  constructor() {
    this.loadStoredLogs()
  }

  private async loadStoredLogs() {
    if (!Capacitor.isNativePlatform()) return

    try {
      const result = await Filesystem.readFile({
        path: this.logFileName,
        directory: Directory.External, // Downloads folder
        encoding: Encoding.UTF8
      })
      
      const storedLogs = JSON.parse(result.data as string)
      this.logs = storedLogs.slice(-this.maxLogSize) // Keep only recent logs
    } catch (error) {
      // File doesn't exist or is corrupted, start fresh
      console.log('No existing log file found, starting fresh')
    }
  }

  private async saveLogsToFile() {
    if (!Capacitor.isNativePlatform()) return

    try {
      await Filesystem.writeFile({
        path: this.logFileName,
        data: JSON.stringify(this.logs),
        directory: Directory.External, // Downloads folder
        encoding: Encoding.UTF8
      })
    } catch (error) {
      console.error('Failed to save logs to file:', error)
    }
  }

  private addLog(level: LogLevel, tag: string, message: string, data?: any) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      tag,
      message,
      data: data ? JSON.stringify(data, null, 2) : undefined
    }

    this.logs.push(entry)

    // Keep only recent logs
    if (this.logs.length > this.maxLogSize) {
      this.logs = this.logs.slice(-this.maxLogSize)
    }

    // Console output with colors
    const levelNames = ['DEBUG', 'INFO', 'WARN', 'ERROR']
    const levelColors = ['gray', 'blue', 'orange', 'red']
    const timestamp = new Date().toLocaleTimeString()
    
    console.log(
      `%c[${timestamp}] ${levelNames[level]} ${tag}: ${message}`,
      `color: ${levelColors[level]}`
    )
    
    if (data) {
      console.log(data)
    }

    // Save to file periodically
    if (this.logs.length % 10 === 0) {
      this.saveLogsToFile()
    }
  }

  debug(tag: string, message: string, data?: any) {
    if (this.logLevel <= LogLevel.DEBUG) {
      this.addLog(LogLevel.DEBUG, tag, message, data)
    }
  }

  info(tag: string, message: string, data?: any) {
    if (this.logLevel <= LogLevel.INFO) {
      this.addLog(LogLevel.INFO, tag, message, data)
    }
  }

  warn(tag: string, message: string, data?: any) {
    if (this.logLevel <= LogLevel.WARN) {
      this.addLog(LogLevel.WARN, tag, message, data)
    }
  }

  error(tag: string, message: string, data?: any) {
    if (this.logLevel <= LogLevel.ERROR) {
      this.addLog(LogLevel.ERROR, tag, message, data)
    }
  }

  async exportLogs(): Promise<string> {
    // Force save current logs
    await this.saveLogsToFile()
    
    const logText = this.logs
      .map(log => {
        const levelNames = ['DEBUG', 'INFO', 'WARN', 'ERROR']
        let output = `[${log.timestamp}] ${levelNames[log.level]} ${log.tag}: ${log.message}`
        if (log.data) {
          output += `\nData: ${log.data}`
        }
        return output
      })
      .join('\n\n')

    return logText
  }

  async clearLogs() {
    this.logs = []
    await this.saveLogsToFile()
  }

  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logs.slice(-count)
  }

  setLogLevel(level: LogLevel) {
    this.logLevel = level
  }

  // Location-specific logging helpers
  locationDebug(message: string, data?: any) {
    this.debug('LOCATION', message, data)
  }

  locationInfo(message: string, data?: any) {
    this.info('LOCATION', message, data)
  }

  locationWarn(message: string, data?: any) {
    this.warn('LOCATION', message, data)
  }

  locationError(message: string, data?: any) {
    this.error('LOCATION', message, data)
  }

  // Background tracking logging helpers
  backgroundDebug(message: string, data?: any) {
    this.debug('BACKGROUND', message, data)
  }

  backgroundInfo(message: string, data?: any) {
    this.info('BACKGROUND', message, data)
  }

  backgroundWarn(message: string, data?: any) {
    this.warn('BACKGROUND', message, data)
  }

  backgroundError(message: string, data?: any) {
    this.error('BACKGROUND', message, data)
  }

  // API logging helpers
  apiDebug(message: string, data?: any) {
    this.debug('API', message, data)
  }

  apiInfo(message: string, data?: any) {
    this.info('API', message, data)
  }

  apiWarn(message: string, data?: any) {
    this.warn('API', message, data)
  }

  apiError(message: string, data?: any) {
    this.error('API', message, data)
  }
}

// Export singleton instance
export const logger = new Logger()

// Helper function to format location data for logging
export function formatLocationForLog(location: any) {
  return {
    lat: location.latitude?.toFixed(6),
    lng: location.longitude?.toFixed(6),
    accuracy: location.accuracy,
    altitude: location.altitude,
    speed: location.speed,
    timestamp: location.timestamp,
    source: location.source || 'unknown'
  }
}

// Helper function to format API response for logging
export function formatApiResponseForLog(response: any) {
  return {
    status: response.status,
    statusText: response.statusText,
    url: response.config?.url,
    method: response.config?.method,
    data: response.data
  }
}